import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import {
  INTEGRATION_KINDS,
  INTEGRATION_KIND_LIST,
  type TenantIntegrationKind
} from "../lib/integrationKinds.js";
import type { AiProviderStatus } from "../lib/aiProvider.js";

/**
 * Tenant admin — Create integration (ADR-0085 Phase 2 §5.3).
 *
 * Three-step single-page flow: kind picker → name → kind-specific sub-form.
 * Single Create submit. Validation errors render as a top-of-form banner +
 * per-field inline errors.
 */

const NAME_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
const NAME_MAX_LEN = 64;

export function IntegrationNew(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();

  const [searchParams] = useSearchParams();
  // ?kind=ai_provider (the Settings and AI-panel links) preselects a kind.
  const requestedKind = searchParams.get("kind");
  const [kind, setKind] = useState<TenantIntegrationKind>(
    INTEGRATION_KIND_LIST.some((k) => k.kind === requestedKind)
      ? (requestedKind as TenantIntegrationKind)
      : INTEGRATION_KIND_LIST[0]?.kind ?? "plex_api"
  );
  // ADR-0207: at most one AI provider per workspace — the kind is disabled when one exists.
  const [aiProviderExists, setAiProviderExists] = useState(false);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [credentials, setCredentials] = useState<Record<string, unknown>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string> | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const status = await apiFetch<AiProviderStatus>("/api/tenant/ai-provider/status");
        setAiProviderExists(status.state === "connected");
      } catch {
        setAiProviderExists(false); // the hub still refuses a second one (409)
      }
    })();
  }, []);

  const meta = INTEGRATION_KINDS[kind];
  const FormComponent = meta.FormComponent;
  // Microsoft 365 and the AI provider have server-fixed names.
  const hasName = kind !== "microsoft_graph" && kind !== "ai_provider";
  const aiProviderBlocked = kind === "ai_provider" && aiProviderExists;

  function validateName(value: string): string | null {
    if (!value || value.trim().length === 0) return "Name is required.";
    if (value.length > NAME_MAX_LEN) return `Name must be ≤ ${NAME_MAX_LEN} characters.`;
    if (!NAME_PATTERN.test(value)) return "Name must be lowercase letters, digits, and hyphens, starting with a letter or digit.";
    return null;
  }

  async function handleCreate() {
    setBannerError(null);
    setFieldErrors(null);

    // ADR-0155: the Microsoft 365 connection has a server-fixed name and no
    // entered credentials — the POST carries only the kind.
    const isMicrosoft = kind === "microsoft_graph";
    if (hasName) {
      const nameErr = validateName(name.trim());
      if (nameErr) {
        setNameError(nameErr);
        setBannerError("Please fix the highlighted fields.");
        return;
      }
      setNameError(null);
    }

    setBusy(true);
    try {
      const res = await apiFetch<{ integration: { id: string } }>(
        "/api/tenant/admin/integrations",
        {
          method: "POST",
          body: isMicrosoft
            ? { kind }
            : kind === "ai_provider"
              ? { kind, config, credentials }
              : { name: name.trim(), kind, config, credentials }
        }
      );
      toast.push({ kind: "success", message: "Integration created." });
      navigate(`/app/admin/integrations/${res.integration.id}`);
    } catch (e) {
      if (e instanceof ApiRequestError && (e.code === "name_in_use" || e.data?.error === "name_in_use")) {
        setNameError(`An integration named "${name.trim()}" already exists.`);
        setBannerError("Please fix the highlighted fields.");
        return;
      }
      const msg = e instanceof Error ? e.message : String(e);
      setBannerError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageLayout title="New integration">
      <div style={{ display: "grid", gap: 12, maxWidth: 720 }}>
        <div className="card" style={{ display: "grid", gap: 14 }}>
          <p className="muted" style={{ margin: 0 }}>
            {meta.introCopy}
          </p>

          {bannerError ? <ErrorState detail={bannerError} /> : null}

          <label className="field">
            <span className="label">Kind</span>
            <select
              className="select"
              value={kind}
              onChange={(e) => {
                // Each kind's form starts from nothing — another kind's fields must not ride along.
                setKind(e.target.value as TenantIntegrationKind);
                setConfig({});
                setCredentials({});
                setBannerError(null);
              }}
              disabled={INTEGRATION_KIND_LIST.length <= 1}
            >
              {INTEGRATION_KIND_LIST.map((k) => (
                <option key={k.kind} value={k.kind} disabled={k.kind === "ai_provider" && aiProviderExists}>
                  {k.label}
                </option>
              ))}
            </select>
            <div className="help">Choose the kind of external service you're integrating with.</div>
            {aiProviderExists ? (
              <div className="help">Your workspace already has an AI provider. Open it to change it.</div>
            ) : null}
          </label>

          {hasName ? (
          <label className="field">
            <span className="label">Name</span>
            <input
              className="input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setNameError(validateName(name.trim()))}
              placeholder="prod-plex"
            />
            {nameError ? <div className="help" style={{ color: "var(--danger-text)" }}>{nameError}</div> : null}
            <div className="help">
              An internal label so you can identify this integration in Atlas. Plex doesn't see this.
              Lowercase letters, digits, and hyphens; cannot be renamed later.
            </div>
          </label>
          ) : null}

          {aiProviderBlocked ? null : (
          <FormComponent
            mode="create"
            config={config}
            credentials={credentials}
            onChange={(next) => {
              setConfig(next.config);
              setCredentials(next.credentials);
            }}
            errors={fieldErrors}
          />
          )}

          <div className="actions">
            <Link to="/app/admin/integrations" className="btn">
              Cancel
            </Link>
            <button
              type="button"
              className="btn btnPrimary"
              onClick={() => void handleCreate()}
              disabled={busy || aiProviderBlocked}
            >
              {busy ? "Creating…" : "Create integration"}
            </button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
